import React from 'react';
import DayColumn from './DayColumn';
import { Event } from './types'; // Event type is needed for DayColumn

type CalendarViewType = 'day' | 'week';

interface CalendarViewProps {
  view: CalendarViewType;
  weekDays: Date[]; // Used in week view
  currentDate: Date; // Used in day view
  events: Event[];
  onOpenEventModal: (date: Date) => void; // Prop for DayColumn to add event
  onDeleteEvent?: (eventId: string | number) => Promise<void>; // Prop for DayColumn to delete event
}

const CalendarView: React.FC<CalendarViewProps> = ({ 
  view, 
  weekDays, 
  currentDate, 
  events, 
  onOpenEventModal,
  onDeleteEvent 
}) => {
  // Define constant for calendar height (12 hours * 50px per hour + header)
  const calendarHeight = 600; // 12 hours * 50px per hour
  return (
    <div className="overflow-x-auto overflow-y-auto">
      <div className="min-w-[800px]" style={{ height: `${calendarHeight + 50}px` }}>
        {/* Week View */}
        {view === 'week' && (
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            {/* Time column with hour markers */}
            <div className="w-14 pt-12 bg-gray-50 relative">
              {/* Hour markers - offset by header height (48px) */}
              {Array.from({ length: 12 }, (_, i) => i + 8).map((hour) => (
                <div key={hour} className="absolute text-xs text-gray-500 right-2" style={{ top: `${(hour - 8) * 50 + 48}px` }}>
                  {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
                </div>
              ))}
            </div>
            
            <div className="flex-1 flex">
              {weekDays.map((date, index) => (
                <DayColumn 
                  key={index} 
                  date={date} 
                  events={events} 
                  onAddEvent={onOpenEventModal} // Pass the handler
                  onDeleteEvent={onDeleteEvent} // Pass delete handler
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Day View */}
        {view === 'day' && (
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            {/* Time column with hour markers */}
            <div className="w-14 pt-12 bg-gray-50 relative">
              {/* Hour markers - offset by header height (48px) */}
              {Array.from({ length: 12 }, (_, i) => i + 8).map((hour) => (
                <div key={hour} className="absolute text-xs text-gray-500 right-2" style={{ top: `${(hour - 8) * 50 + 48}px` }}>
                  {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
                </div>
              ))}
            </div>
            
            <div className="flex-1">
              <DayColumn 
                date={currentDate} 
                events={events} 
                onAddEvent={onOpenEventModal} // Pass the handler
                onDeleteEvent={onDeleteEvent} // Pass delete handler
              />
            </div>
          </div>
        )}
        
        {/* No month or resource views anymore */}
      </div>
    </div>
  );
};

export default CalendarView; 